import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getWishes, deleteWish } from '../api/wishes';
import Pagination from '../components/Pagination';

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });
}

function groupByMonth(wishes) {
  const groups = {};
  wishes.forEach(w => {
    const date = w.event_date ? new Date(w.event_date) : null;
    const key = date
      ? date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      : 'No date';
    if (!groups[key]) groups[key] = [];
    groups[key].push(w);
  });
  return Object.entries(groups);
}

const SORT_OPTIONS = [
  { value: '',               label: 'Recently added' },
  { value: 'event_date_asc', label: 'Date ↑ (nearest first)' },
  { value: 'event_date_desc',label: 'Date ↓ (furthest first)' }
];

const EVENT_CATEGORIES = ['Theater', 'Show', 'Exhibit'];

function WishList() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [wishes, setWishes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ category: '', timing: '', sort: '' });

  useEffect(() => {
    if (!isAuthenticated) { navigate('/signin'); return; }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchWishes(1, filters);
  }, [filters]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchWishes(currentPage, filters);
  }, [currentPage]);

  const fetchWishes = async (page, activeFilters) => {
    setIsLoading(true);
    const token = localStorage.getItem('auth_token');
    getWishes(token, null, page, null, activeFilters)
      .then(data => {
        setWishes(data.wishes || []);
        if (data.meta) {
          setCurrentPage(data.meta.current_page);
          setTotalPages(data.meta.total_pages);
        }
      })
      .catch(() => setError('Failed to load wishes'))
      .finally(() => setIsLoading(false));
  };

  const setFilter = (key, value) => {
    setCurrentPage(1);
    setFilters(prev => ({ ...prev, [key]: prev[key] === value ? '' : value }));
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem('auth_token');
    await deleteWish(id, token);
    setWishes(wishes.filter(w => w.id !== id));
  };

  if (!isAuthenticated) return null;
  if (error) return <p>{error}</p>;

  return (
    <div className="wish-list">
      <div className="page-header">
        <h1>My Wishes</h1>
      </div>

      {/* Filter bar */}
      <div className="wish-filters">
        {/* Category pills */}
        <div className="wish-filters__group">
          <span className="wish-filters__label">Category</span>
          <div className="wish-filters__pills">
            {EVENT_CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter('category', cat)}
                className={`wish-filter-pill ${filters.category === cat ? 'wish-filter-pill--active' : ''}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Timing toggle */}
        <div className="wish-filters__group">
          <span className="wish-filters__label">Timing</span>
          <div className="wish-filters__pills">
            {['upcoming', 'past'].map(t => (
              <button
                key={t}
                onClick={() => setFilter('timing', t)}
                className={`wish-filter-pill ${filters.timing === t ? 'wish-filter-pill--active' : ''}`}
              >
                {t === 'upcoming' ? '📅 Upcoming' : '⏳ Past'}
              </button>
            ))}
          </div>
        </div>

        {/* Sort select */}
        <div className="wish-filters__group">
          <span className="wish-filters__label">Sort</span>
          <select
            value={filters.sort}
            onChange={e => { setCurrentPage(1); setFilters(prev => ({ ...prev, sort: e.target.value })); }}
            className="wish-filters__select"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Active filters — reset button */}
        {(filters.category || filters.timing || filters.sort) && (
          <button
            onClick={() => setFilters({ category: '', timing: '', sort: '' })}
            className="wish-filters__reset"
          >
            ✕ Clear filters
          </button>
        )}
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : wishes.length === 0 ? (
        <p className="wish-list__empty">
          {filters.category || filters.timing
            ? 'No wishes match these filters.'
            : <>No wishes yet. <Link to="/events">Browse events</Link> to add some!</>}
        </p>
      ) : (
        groupByMonth(wishes).map(([monthYear, items]) => (
          <div key={monthYear} className="moment-group">
            <h2 className="moment-group__heading">{monthYear}</h2>
            <div className="wish-cards">
              {items.map(wish => (
                <div key={wish.id} className="wish-card">
                  {wish.event_url_image && (
                    <div className="wish-card__image-wrapper">
                      <img
                        src={wish.event_url_image}
                        alt={wish.event_name}
                        className="wish-card__image"
                      />
                    </div>
                  )}
                  <div className="wish-card__body">
                    <div className="wish-card__meta">
                      {wish.event_category && (
                        <span className="wish-card__category">{wish.event_category}</span>
                      )}
                      {wish.event_date && (
                        <span className="wish-card__date">📅 {formatDate(wish.event_date)}</span>
                      )}
                    </div>
                    <h2 className="wish-card__title">
                      <Link to={`/events/${wish.event_id}`}>
                        {wish.event_name || `Event #${wish.event_id}`}
                      </Link>
                    </h2>
                    {wish.event_description && (
                      <p className="wish-card__description">
                        {wish.event_description.length > 120
                          ? wish.event_description.slice(0, 120) + '…'
                          : wish.event_description}
                      </p>
                    )}
                    <div className="wish-card__actions">
                      <Link to={`/events/${wish.event_id}`} className="btn btn-primary btn-sm">
                        View event
                      </Link>
                      <button
                        onClick={() => handleDelete(wish.id)}
                        className="wish-card__remove"
                        aria-label="Remove from wishes"
                      >
                        ♥ Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}

export default WishList;
